import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardTitle,
  Table,
  Badge,
  Spinner,
  Alert,
  Row,
  Col,
  Input,
  InputGroup,
  InputGroupText,
  Button,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from 'reactstrap';
import axios from '../../api/Axios';

const TutoringProviders = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, individual, entity
  const navigate = useNavigate();

  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/tutoring-providers/');
      setProviders(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching tutoring providers:', err);
      setError('Failed to load tutoring providers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (regStatus) => {
    const statusMap = {
      0: { color: 'warning', text: 'Pending' },
      1: { color: 'info', text: 'In Progress' },
      2: { color: 'success', text: 'Complete' },
      3: { color: 'danger', text: 'Rejected' }
    };
    const status = statusMap[regStatus] || { color: 'secondary', text: 'Unknown' };
    return <Badge color={status.color}>{status.text}</Badge>;
  };

  const getVerificationBadge = (isVerified) => {
    return isVerified ? (
      <Badge color="success">
        <i className="bi bi-check-circle me-1"></i>
        Verified
      </Badge>
    ) : (
      <Badge color="warning">
        <i className="bi bi-clock me-1"></i>
        Pending
      </Badge>
    );
  };

  const getProviderTypeBadge = (type) => {
    return type === 'individual' ? (
      <Badge color="primary">
        <i className="bi bi-person me-1"></i>
        Individual
      </Badge>
    ) : (
      <Badge color="info">
        <i className="bi bi-building me-1"></i>
        Entity
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Handler functions for dropdown actions
  const handleView = (provider) => {
    navigate(`/backoffice-dashboard/tutoring-provider/${provider.id}?type=${provider._type}`);
  };

  const handleDelete = (provider) => {
    if (window.confirm(`Are you sure you want to delete ${provider._type === 'entity' ? provider.company_name : provider.first_name + ' ' + provider.last_name}?`)) {
      // TODO: Implement delete API call
      alert('Delete functionality to be implemented');
    }
  };

  // Filter providers based on search and type
  const filteredProviders = providers.filter(provider => {
    const matchesSearch = 
      (provider.first_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (provider.last_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (provider.email?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (provider.company_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (provider.provider_code?.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesType = 
      filterType === 'all' ||
      provider._type === filterType;

    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <p className="mt-3">Loading tutoring providers...</p>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">
          <i className="bi bi-mortarboard me-2"></i>
          Tutoring Providers
        </h2>
        <Button 
          color="primary" 
          onClick={() => navigate('/backoffice-dashboard/create-tutoring-agent')}
          className="d-flex align-items-center"
        >
          <i className="fas fa-plus me-2"></i>
          ADD +
        </Button>
      </div>

      {error && (
        <Alert color="danger" className="mb-4">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}

      <Row>
        <Col md="12">
          <Card>
            <CardBody>
              <CardTitle tag="h5">
                <Badge color="primary" className="ms-2">
                  {filteredProviders.length} Total
                </Badge>
              </CardTitle>

              {/* Search and Filter Controls */}
              <Row className="mb-4">
                <Col md="6">
                  <InputGroup>
                    <InputGroupText>
                      <i className="bi bi-search"></i>
                    </InputGroupText>
                    <Input
                      type="text"
                      placeholder="Search by name, email, company, or code..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </Col>
                <Col md="3">
                  <Input
                    type="select"
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                  >
                    <option value="all">All Types</option>
                    <option value="individual">Individual Only</option>
                    <option value="entity">Entity Only</option>
                  </Input>
                </Col>
              </Row>

              {/* Providers Table */}
              {filteredProviders.length === 0 ? (
                <Alert color="info">
                  <i className="bi bi-info-circle me-2"></i>
                  {searchTerm || filterType !== 'all' 
                    ? 'No tutoring providers match your search criteria.' 
                    : 'No tutoring providers found in the system.'}
                </Alert>
              ) : (
                <div className="table-responsive">
                  <Table hover size="sm">
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Type</th>
                        <th>Name/Company</th>
                        <th>Representative</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th className="text-center">ID</th>
                        <th className="text-center">Email</th>
                        <th className="text-center">Status</th>
                        <th>Created</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProviders.map((provider) => (
                        <tr key={`${provider._type}-${provider.id}`}>
                          <td>
                            <code className="text-primary">{provider.provider_code || 'N/A'}</code>
                          </td>
                          <td>
                            {getProviderTypeBadge(provider._type)}
                          </td>
                          <td>
                            {provider._type === 'entity' ? (
                              provider.company_name || 'N/A'
                            ) : (
                              `${provider.title || ''} ${provider.first_name || ''} ${provider.last_name || ''}`.trim() || 'N/A'
                            )}
                          </td>
                          <td>
                            {provider._type === 'entity' ? (
                              `${provider.title || ''} ${provider.first_name || ''} ${provider.last_name || ''}`.trim() || 'N/A'
                            ) : (
                              '-'
                            )}
                          </td>
                          <td>
                            <small>{provider.email || 'N/A'}</small>
                          </td>
                          <td>
                            <small>{provider.contact_phone || 'N/A'}</small>
                          </td>
                          <td className="text-center">
                            {getVerificationBadge(provider.id_verified)}
                          </td>
                          <td className="text-center">
                            {getVerificationBadge(provider.email_verified)}
                          </td>
                          <td className="text-center">
                            {getStatusBadge(provider.reg_status)}
                          </td>
                          <td>
                            <small className="text-muted">
                              {formatDate(provider.created_at)}
                            </small>
                          </td>
                          <td className="text-center">
                            <UncontrolledDropdown>
                              <DropdownToggle 
                                color="outline-primary" 
                                size="sm" 
                                caret={false}
                              >
                                <i className="bi bi-three-dots"></i>
                              </DropdownToggle>
                              <DropdownMenu end>
                                <DropdownItem onClick={() => handleView(provider)}>
                                  <i className="bi bi-eye me-2"></i>
                                  View
                                </DropdownItem>
                                <DropdownItem divider />
                                <DropdownItem 
                                  onClick={() => handleDelete(provider)}
                                  className="text-danger"
                                >
                                  <i className="bi bi-trash me-2"></i>
                                  Delete
                                </DropdownItem>
                              </DropdownMenu>
                            </UncontrolledDropdown>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              {/* Summary Cards */}
              {providers.length > 0 && (
                <Row className="mt-4">
                  <Col md="3">
                    <Card className="border-0 bg-light">
                      <CardBody>
                        <h6 className="text-muted mb-2">Total Providers</h6>
                        <h3 className="mb-0">{providers.length}</h3>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md="3">
                    <Card className="border-0 bg-light">
                      <CardBody>
                        <h6 className="text-muted mb-2">Individual</h6>
                        <h3 className="mb-0 text-primary">
                          {providers.filter(p => p._type === 'individual').length}
                        </h3>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md="3">
                    <Card className="border-0 bg-light">
                      <CardBody>
                        <h6 className="text-muted mb-2">Entity</h6>
                        <h3 className="mb-0 text-info">
                          {providers.filter(p => p._type === 'entity').length}
                        </h3>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md="3">
                    <Card className="border-0 bg-light">
                      <CardBody>
                        <h6 className="text-muted mb-2">Verified</h6>
                        <h3 className="mb-0 text-success">
                          {providers.filter(p => p.id_verified && p.email_verified).length}
                        </h3>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default TutoringProviders;
